import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async'; // SEO başlıkları için

const API_URL = process.env.REACT_APP_API_URL; // Fallback eklendi

const BlogHomePage = () => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlogPosts = async () => {
            try {
                // Backend'deki /posts endpoint'ine istek atıyoruz
                const response = await axios.get(`${API_URL}/posts`);
                setPosts(response.data);
            } catch (err) {
                console.error("API Hatası:", err);
                setError("İçerikler yüklenirken bir bağlantı hatası oluştu.");
            } finally {
                setLoading(false);
            }
        };

        fetchBlogPosts();
    }, []);

    // Tarih formatlama fonksiyonu
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString('tr-TR', options);
    };

    // İçerik Özeti Oluşturucu (HTML taglerini temizlemek için basit bir yöntem)
    const getSummary = (content) => {
        if (!content) return "";
        // HTML etiketlerini temizle ve ilk 120 karakteri al
        return content.replace(/<[^>]*>?/gm, '').substring(0, 120) + "...";
    };

    // Yükleniyor Ekranı
    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-900">
                <div className="flex flex-col items-center">
                    <div className="w-16 h-16 border-4 border-t-4 border-teal-500 border-gray-700 rounded-full animate-spin"></div>
                    <p className="mt-4 text-gray-400 animate-pulse">İçerikler hazırlanıyor...</p>
                </div>
            </div>
        );
    }

    // Hata Ekranı
    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-900 text-center px-4">
                <div className="max-w-md bg-gray-800 p-8 rounded-xl shadow-2xl border border-red-500/30">
                    <h2 className="text-2xl font-bold text-red-500 mb-2">Bir Sorun Oluştu</h2>
                    <p className="text-gray-300">{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="mt-6 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                    >
                        Sayfayı Yenile
                    </button>
                </div>
            </div>
        );
    }

    return (
        <main className="min-h-screen bg-gray-900 text-white font-inter py-16 px-4 sm:px-6 lg:px-8">
            
            <Helmet>
                <title>Mekânname Blog | Tarih, Kültür ve Teknoloji Keşifleri</title>
                <meta name="description" content="Türkiye'nin tarihi mekanlarını, kültürel miraslarını ve teknolojik yeniliklerini Mekânname Blog ile keşfedin." />
            </Helmet>

            <div className="max-w-7xl mx-auto">
                <header className="text-center mb-16">
                    <h1 className="text-5xl md:text-7xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-blue-500 to-indigo-600 mb-6 drop-shadow-2xl tracking-tight">
                        Mekânname Blog
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
                        Geçmişin izlerini sürerken geleceğin teknolojisine dokunun. 
                        <span className="text-teal-400 font-semibold"> Keşfetmeye buradan başlayın.</span>
                    </p>
                </header>

                {posts.length === 0 ? (
                    <div className="text-center py-20 bg-gray-800/50 rounded-3xl border border-gray-700">
                        <p className="text-2xl text-gray-400">Henüz yayınlanmış bir blog yazısı bulunmuyor.</p>
                        <p className="text-gray-500 mt-2">Lütfen daha sonra tekrar kontrol edin.</p>
                    </div>
                ) : (
                    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                        {posts.map((post) => (
                            <article 
                                key={post.id} 
                                className="group relative flex flex-col h-full bg-gray-800 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-teal-500/20 border border-gray-700 transition-all duration-500 hover:-translate-y-2"
                            >
                                <Link to={`/blog/${post.id}`} className="block flex-grow h-full" aria-label={`${post.title} yazısını oku`}>
                                    
                                    {/* Görsel Alanı */}
                                    <div className="relative overflow-hidden h-64">
                                        <img
                                            src={post.image_url || "https://placehold.co/600x400/1f2937/14b8a6?text=Mekanname"} 
                                            alt={post.title} 
                                            loading="lazy" 
                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `https://placehold.co/600x400/1f2937/14b8a6?text=${encodeURIComponent(post.title)}`;
                                            }}
                                        />
                                    </div>

                                    {/* İçerik Alanı */}
                                    <div className="p-6 flex flex-col flex-grow relative z-10 bg-gray-800">
                                        {/* Metadata (Tarih - Yazar - Süre) */}
                                        <div className="flex flex-wrap items-center text-xs text-gray-400 mb-3 gap-2">
                                            {/* Backend'den gelen 'date' alanını kullanıyoruz */}
                                            <time dateTime={post.date} className="flex items-center">
                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                                {formatDate(post.date)}
                                            </time>
                                            
                                            {post.author && (
                                                <span className="flex items-center">
                                                    <span className="mx-1">•</span>
                                                    <span>{post.author}</span>
                                                </span>
                                            )}

                                            {/* Okuma Süresi (Backend'den geliyorsa) */}
                                            {post.read_time && (
                                                <span className="flex items-center">
                                                    <span className="mx-1">•</span>
                                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                                    {post.read_time}
                                                </span>
                                            )}
                                        </div>

                                        <h2 className="text-2xl font-bold text-white mb-3 leading-snug group-hover:text-teal-400 transition-colors line-clamp-2">
                                            {post.title}
                                        </h2>

                                        <p className="text-gray-300 text-sm mb-6 line-clamp-3 leading-relaxed flex-grow">
                                            {getSummary(post.content)}
                                        </p>

                                        {/* Alt Bar */}
                                        <div className="mt-auto pt-4 border-t border-gray-700 flex items-center justify-between">
                                            <span className="text-teal-400 text-sm font-semibold group-hover:underline decoration-teal-500/50 underline-offset-4">
                                                Devamını Oku
                                            </span>
                                            <svg className="w-5 h-5 text-teal-400 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </div>
                                    </div>
                                </Link>
                            </article>
                        ))}
                    </section>
                )}
            </div>
        </main>
    );
};

export default BlogHomePage;